/**
 * Bear-style colored text highlights.
 *
 * Syntax (stored inside the note's markdown — this is the synced format):
 *   ==text==            → default highlight (yellow)
 *   ==green:text==      → colored highlight; color is a palette NAME
 *
 * The color is always a name from {@link HIGHLIGHT_COLORS}, resolved to a
 * theme token (`--color-hl-*`) at render time — never a hex value. This module
 * is the single source of truth for the syntax; the web app (TipTap), the
 * share viewer (react-markdown), the worker (plain-text strip), and the Swift
 * editor all mirror this exact contract. **If you change the regex, change it
 * here** (and mirror it in `apps/swift/.../BlockModelParser.swift`).
 */

export const HIGHLIGHT_COLORS = [
  "yellow",
  "green",
  "blue",
  "pink",
  "purple",
] as const

export type HighlightColor = (typeof HIGHLIGHT_COLORS)[number]

export const DEFAULT_HIGHLIGHT: HighlightColor = "yellow"

/**
 * Matches `==text==` and `==color:text==`.
 *
 * - The color group is restricted to the known palette names, so `==Note: x==`
 *   does NOT parse "Note" as a color — the whole "Note: x" becomes a default
 *   highlight instead.
 * - `(?=\S) ... (?<=\S)` forces the delimiters to hug the text, so arithmetic
 *   like `2 == 2` and empty `==  ==` never match.
 * - The text group is non-greedy, so `==a== ==b==` yields two separate matches.
 *
 * Group 1 = color name (optional), group 2 = inner text.
 */
export const HIGHLIGHT_REGEX =
  /==(?:(yellow|green|blue|pink|purple):)?(?=\S)([^\n]+?)(?<=\S)==/g

export interface Highlight {
  color: HighlightColor
  text: string
}

/** Extract all highlights from markdown, in document order. */
export function extractHighlights(content: string): Highlight[] {
  const out: Highlight[] = []
  HIGHLIGHT_REGEX.lastIndex = 0
  let match: RegExpExecArray | null
  while ((match = HIGHLIGHT_REGEX.exec(content)) !== null) {
    out.push({
      color: (match[1] as HighlightColor) ?? DEFAULT_HIGHLIGHT,
      text: match[2],
    })
  }
  return out
}

/**
 * Replace highlight syntax with its inner text — for plain-text surfaces
 * (previews, notification bodies, search snippets, exports).
 * `==green:done==` → `done`.
 */
export function stripHighlights(md: string): string {
  return md.replace(HIGHLIGHT_REGEX, "$2")
}
